import React from 'react';

import { Button, PasswordInput, Space } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { PasswordInputWithStrength } from 'components/PasswordInputWithValidation';
import { useUserContext } from 'contexts/UserContext';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { UserService } from 'services/user-service';
import { confirmPasswordValidator, requiredInputValidator } from 'shared/utils/form-validators';

export function PasswordSettings() {
  const [user] = useUserContext();

  const { t } = useTranslation();

  const passwordResetForm = useForm({
    initialValues: { currentPassowrd: '', newPassword: '', confirmPassword: '' },
    validate: {
      currentPassowrd: (value) => requiredInputValidator(value),
      newPassword: (value) => requiredInputValidator(value),
      confirmPassword: (value, values) =>
        requiredInputValidator(value) || confirmPasswordValidator(values.newPassword, value)
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: () =>
      UserService.updatePassword({
        email: user.email,
        oldPassword: passwordResetForm.values.currentPassowrd,
        newPassword: passwordResetForm.values.newPassword
      }),
    onSuccess: () => {
      passwordResetForm.reset();
      NotificationService.showResult(true, t('AUTH.RESET_PASSWORD.SUCCESS'));
    },
    onError: () => NotificationService.showResult(false, t('AUTH.RESET_PASSWORD.ERROR'))
  });

  const savePasswordChanges = () => {
    const isNotValid = passwordResetForm.validate().hasErrors;
    if (isNotValid) return;
    resetPasswordMutation.mutate();
  };

  return (
    <>
      <PasswordInput
        label={t('AUTH.PASSWORD.LABEL')}
        placeholder={t('AUTH.PASSWORD.PLACEHOLDER')}
        {...passwordResetForm.getInputProps('currentPassowrd')}></PasswordInput>
      <Space h={15} />
      <PasswordInputWithStrength
        passwordInputProps={passwordResetForm.getInputProps('newPassword')}
        confirmPasswordInputProps={passwordResetForm.getInputProps('confirmPassword')}
      />
      <Space h={20} />
      <Button size="xs" loading={resetPasswordMutation.isLoading} onClick={() => savePasswordChanges()}>
        {t('SETTINGS.PASSWORD.UPDATE_PASSWORD')}
      </Button>
    </>
  );
}
